import './index.scss';

import type { ReactNode } from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { CButton } from '@/components/CButton';
import { Icon } from '@/components/Icon';
import { splitLastWord } from '@/components/PageBanner';

const WIDTHS = { md: 560, lg: 760 } as const;

type HeroModalProps = {
  open: boolean;
  onClose: () => void;
  /** The title; its last word is set on the signal highlight. */
  title: string;
  /** Small label above the title. */
  badge?: string;
  description?: string;
  size?: keyof typeof WIDTHS;
  /** Rendered in the dialog's action band. */
  footer?: ReactNode;
  children: ReactNode;
};

/**
 * Dialog with the Keyvod identity header (`mz-modal__hero`): the Blue → Violet brand field with the
 * badge, the display title and its description; the body scrolls under it.
 */
export const HeroModal = ({
  open,
  onClose,
  title,
  badge,
  description,
  size = 'md',
  footer,
  children,
}: HeroModalProps) => {
  const { t } = useTranslation(['common']);
  const [lead, highlight] = splitLastWord(title);

  return (
    <Modal
      open={open}
      centered
      width={WIDTHS[size]}
      closable={false}
      footer={footer ?? null}
      className="c-hero-modal"
      title={
        <div className="c-hero-modal__hero">
          <div className="c-hero-modal__heading">
            {badge ? <span className="c-hero-modal__badge">{badge}</span> : null}
            <h2 className="c-hero-modal__title">
              {lead}
              <span className="c-hero-modal__highlight">{highlight}</span>
            </h2>
            {description ? <p className="c-hero-modal__description">{description}</p> : null}
          </div>
          <CButton
            iconOnly
            variant="ghost"
            size="sm"
            className="c-hero-modal__close"
            startIcon={<Icon name="close" size={18} strokeWidth={1.8} />}
            aria-label={t('common:common.close')}
            onClick={onClose}
          />
        </div>
      }
      onCancel={onClose}
    >
      {children}
    </Modal>
  );
};
